import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { DbNewsArticle } from "@/types/database";
import { newsCategories } from "@/mocks/eventsData";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

type CategoryKey = string;

export default function NewsList() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [news, setNews] = useState<DbNewsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNews() {
      const { data, error } = await supabase
        .from("news_articles")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true });
      if (!error && data) {
        setNews(data as DbNewsArticle[]);
      }
      setLoading(false);
    }
    fetchNews();
  }, []);

  const filtered = useMemo(() => {
    return news.filter((n) => {
      const matchCategory = activeCategory === "all" || n.category === activeCategory;
      const matchSearch =
        !searchQuery ||
        n.title.includes(searchQuery) ||
        (n.summary && n.summary.includes(searchQuery));
      return matchCategory && matchSearch;
    });
  }, [activeCategory, searchQuery, news]);

  const featured = filtered.find((n) => n.is_featured);
  const regular = filtered.filter((n) => n.id !== featured?.id);

  return (
    <section id="news" className="py-24 md:py-32 bg-silver-50">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Latest News
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
              最新消息
            </h2>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-80">
            <Icon className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-silver-400" />
            <input
              type="text"
              placeholder="搜尋新聞標題..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-silver-200 rounded-full text-sm text-navy-950 placeholder:text-silver-400 focus:outline-none focus:border-navy-700 transition-colors"
            />
          </div>
        </div>

        {/* Category filter tabs */}
        <div className="flex flex-wrap gap-2 mb-12 md:mb-16">
          {newsCategories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === cat.key
                  ? "bg-navy-950 text-white"
                  : "bg-white text-silver-600 hover:bg-silver-100 border border-silver-200"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
            <p className="text-sm text-silver-400">載入消息中...</p>
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid lg:grid-cols-4 gap-6 md:gap-8">
            {/* Featured news - large left card */}
            {featured && (
              <article className="group lg:col-span-2 lg:row-span-2 relative rounded-3xl overflow-hidden bg-navy-950 min-h-[320px] md:min-h-full flex flex-col justify-end">
                <SmartImage
                  src={featured.image || ""}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  fallbackText={featured.category}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/60 to-transparent" />

                <div className="relative z-10 p-6 md:p-8 lg:p-10">
                  <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full mb-4">
                    {featured.category}
                  </span>
                  <p className="text-silver-400 text-sm mb-3">{featured.date}</p>
                  <h3 className="text-white font-semibold text-xl md:text-2xl lg:text-3xl mb-3 font-serif leading-snug">
                    {featured.title}
                  </h3>
                  <p className="text-silver-300 text-sm md:text-base leading-relaxed mb-5">
                    {featured.summary || ""}
                  </p>
                  {featured.link_url && (
                    <a
                      href={featured.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-white hover:text-silver-200 transition-colors"
                    >
                      閱讀完整內容
                      <Icon className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </a>
                  )}
                </div>
              </article>
            )}

            {/* Regular news cards */}
            {regular.map((newsItem) => (
              <article
                key={newsItem.id}
                className="group bg-white rounded-3xl overflow-hidden border border-silver-200 hover:border-navy-200 hover:shadow-xl transition-all duration-500 flex flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden flex-shrink-0">
                  <SmartImage
                    src={newsItem.image || ""}
                    alt={newsItem.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    fallbackText={newsItem.category}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/30 to-transparent" />
                </div>

                <div className="p-5 md:p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs font-medium text-navy-700 bg-navy-50 px-2.5 py-1 rounded-full">
                      {newsItem.category}
                    </span>
                    <span className="text-xs text-silver-400">{newsItem.date}</span>
                  </div>
                  <h3 className="text-navy-950 font-semibold text-base md:text-lg mb-2 group-hover:text-navy-700 transition-colors line-clamp-2 leading-snug">
                    {newsItem.title}
                  </h3>
                  <p className="text-silver-500 text-sm leading-relaxed line-clamp-2 flex-grow">
                    {newsItem.summary || ""}
                  </p>
                  {newsItem.link_url && (
                    <div className="mt-4 pt-4 border-t border-silver-100">
                      <a
                        href={newsItem.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-navy-950 hover:text-navy-700 transition-colors"
                      >
                        閱讀更多
                        <Icon className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Icon className="ri-newspaper-line text-5xl text-silver-300 mb-4" />
            <p className="text-silver-500 text-lg mb-2">沒有符合條件的消息</p>
            <p className="text-silver-400 text-sm">請嘗試其他篩選條件或搜尋關鍵字</p>
          </div>
        )}
      </div>
    </section>
  );
}